import { AllowedProvidersUrl, findOembedProviderUrl } from '@loyalty/shared-common';
import { Editor } from 'grapesjs';
import {
  EmbeddedMediaComponentAttributes,
  EmbeddedMediaRenderer,
  SpotifySize,
} from '@loyalty/editor-components';
import { facMedia } from '@qualifio/ui-commons';
import { EditorComponentDefinition } from '../types';
import { EditorContext } from '../../../services/editor-service';
import { EditorEmbeddedMediaModal } from './EditorEmbeddedMediaModal';
import {
  EmbeddedMediaWidgetTraits,
  embeddedMediaWidgetTraitsSchema,
} from './EmbeddedMediaWidgetTraits';
import { buildParticipantContextFromGrape } from '../../utils';
import { translateAttributes } from '../../../services/copy-paste/copy-paste-utils';

export const embeddedMediaComponentDefinition: EditorComponentDefinition = {
  id: 'embedded-media',
  icon: facMedia,
  category: 'Interactive elements',
  getLabel: ({ t }) => t('editor.component.embedded-media'),
  component: EmbeddedMediaWidget,
  traits: {
    schema: embeddedMediaWidgetTraitsSchema,
    form: EmbeddedMediaWidgetTraits,
  },
  copyPasteHelper: (element, context) => translateAttributes(element, context.destinationLocales),
};

function EmbeddedMediaWidget(editor: Editor) {
  const context: EditorContext = editor.Config.context;
  const i18n = editor.I18n;
  const i18nAttributes: any = Object.fromEntries(
    context.languages.map((l: string) => [
      l,
      {
        targetUrl: undefined,
        spotifySize: undefined,
      },
    ]),
  );

  editor.DomComponents.addType('embedded-media', {
    view: {
      defaults: {
        attributes: {
          targetUrl: undefined,
        },
        editable: true,
        traits: [],
        i18nAttributes,
      },
      onRender() {
        const { el, model } = this;
        el.innerHTML = model.toHTML();
      },
      events: {
        dblclick: 'onDoubleClick',
      },
      onDoubleClick() {
        const i18nAttributes = this.model.attributes.i18nAttributes?.[i18n.getLocale()];

        (editor.getConfig('context') as EditorContext).openModal(props => ({
          content: (
            <EditorEmbeddedMediaModal
              {...props}
              data={{
                targetUrl: i18nAttributes?.targetUrl,
                spotifySize: i18nAttributes?.spotifySize,
              }}
              callback={(data: EmbeddedMediaComponentAttributes) => {
                this.model.addAttributes({ targetUrl: data.targetUrl });
                if (data.spotifySize) {
                  this.model.addAttributes({ spotifySize: data.spotifySize });
                }
                this.model.attributes.i18nAttributes = {
                  ...this.model.attributes.i18nAttributes,
                  [i18n.getLocale()]: data,
                };
                this.onRender?.();
              }}
            />
          ),
          dialogProps: {
            maxWidth: 'sm',
          },
        }));
      },
    },
    model: {
      defaults: {
        attributes: {
          targetUrl: '',
          spotifySize: SpotifySize.SMALL,
        },
        draggable: '[data-gjs-type=column]',
        droppable: false,
        editable: true,
        traits: [],
        i18nAttributes,
      },
      toHTML() {
        const targetUrl = this.attributes.i18nAttributes?.[i18n.getLocale()]?.targetUrl;
        const providerUrl = findOembedProviderUrl(targetUrl);
        const isSpotify = providerUrl === AllowedProvidersUrl.SPOTIFY;

        return `
          <div class="qlfeditor-embedded-media-container">
          <div style="${isSpotify ? '' : 'min-height: 200px'}">
            ${EmbeddedMediaRenderer(
              {
                state: context.widgetState,
                options: this.attributes,
                context: buildParticipantContextFromGrape(i18n),
              },
              editor.Canvas.getDocument(),
            )}
          </div>
          </div>
        `;
      },
    },
  });
}
